import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useConfirm } from '../components/common/ConfirmDialog';
import { pageVariants, sectionVariants } from '../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  PersonRemove as PersonRemoveIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  AccessTime as AccessTimeIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Fingerprint as FingerprintIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { hasMinRole, isReadOnly, ROLES } from '../utils/roles';
import { isPasswordValid, getPasswordErrorMessage } from '../utils/passwordValidation';
import { isCustomerSubdomain } from '../utils/constants';
import PasswordStrengthIndicator from '../components/common/PasswordStrengthIndicator';

const AdminManagement = () => {
  const confirm = useConfirm();
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [showPromoteForm, setShowPromoteForm] = useState(false);
  const [promoteRole, setPromoteRole] = useState('sso_admin'); // 'sso_admin' | 'read_only'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // SSO session settings state
  const [ssoSessionTimeout, setSsoSessionTimeout] = useState(720); // Default 12 hours in minutes
  const [loadingSsoSettings, setLoadingSsoSettings] = useState(false);
  const [savingSsoSettings, setSavingSsoSettings] = useState(false);
  const [showSsoSettingsDialog, setShowSsoSettingsDialog] = useState(false);
  const [editTimeoutValue, setEditTimeoutValue] = useState(720);

  // Per-user MFA state
  const [mfaUsers, setMfaUsers] = useState([]);
  const [selectedMfaUsers, setSelectedMfaUsers] = useState([]);
  const [loadingMfaUsers, setLoadingMfaUsers] = useState(false);
  const [savingMfa, setSavingMfa] = useState(false);
  const [mfaSearchQuery, setMfaSearchQuery] = useState('');

  // Passkeys realm setting + per-user enforcement method
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const [loadingPasskey, setLoadingPasskey] = useState(false);
  const [savingPasskey, setSavingPasskey] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('otp'); // 'otp' | 'passkey'

  useEffect(() => {
    fetchAdmins();

    if (isCustomerSubdomain()) {
      const adminDetails = localStorage.getItem('adminDetails');
      if (adminDetails) {
        const parsed = JSON.parse(adminDetails);
        setCurrentUser({
          ...parsed,
          role: parsed.role || 'sso_admin',
          isAdmin: parsed.isAdmin || true
        });
      }
      // Fetch SSO session settings and MFA status for customer realms
      fetchSsoSessionSettings();
      fetchMfaUsers();
      fetchPasskeysSettings();
    } else {
      const appAdminDetails = localStorage.getItem('appAdminDetails');
      if (appAdminDetails) {
        const parsed = JSON.parse(appAdminDetails);
        setCurrentUser({
          username: parsed.username,
          role: parsed.isAppAdmin ? 'admin' : 'sso_admin',
          isAdmin: parsed.isAppAdmin || false
        });
      }
    }
  }, []);

  const fetchAdmins = async () => {
    setRefreshing(true);

    try {
      const response = await apiService.listAdmins();

      if (response.success) {
        let adminsList = [];
        if (response.data.admins) {
          adminsList = response.data.admins;
        } else if (Array.isArray(response.data)) {
          adminsList = response.data;
        }
        setAdmins(adminsList);
      } else {
        toast.error('Failed to fetch admin users');
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      toast.error(err.message || 'Failed to fetch admin users');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchUsers = async () => {
    setFetchingUsers(true);
    try {
      const response = await apiService.getAllUsers({ page: 1, pageSize: 1000 });
      if (response.success) {
        const usersList = response.data.users || [];
        setUsers(usersList);
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error(err.message || 'Failed to fetch users');
    } finally {
      setFetchingUsers(false);
    }
  };

  const handleOpenPromoteForm = () => {
    setShowPromoteForm(true);
    fetchUsers();
  };

  const handlePromoteUser = async () => {
    if (selectedUsers.length === 0) return;

    setLoading(true);

    const successes = [];
    const failures = [];

    for (const user of selectedUsers) {
      try {
        const response = await apiService.promoteUserToAdmin(user.username, promoteRole);
        if (response.success) {
          successes.push(user.username);
        } else {
          failures.push({ username: user.username, error: 'Promotion failed' });
        }
      } catch (err) {
        failures.push({ username: user.username, error: err.message });
      }
    }

    if (failures.length === 0) {
      toast.success(`${successes.length} user${successes.length > 1 ? 's' : ''} promoted to admin successfully`);
    } else if (successes.length > 0) {
      const failMsgs = failures.slice(0, 3).map(f => `${f.username}: ${f.error}`).join(', ');
      const extra = failures.length > 3 ? ` and ${failures.length - 3} more` : '';
      toast.warning(`${successes.length} promoted, ${failures.length} failed — ${failMsgs}${extra}`);
    } else {
      const failMsgs = failures.slice(0, 3).map(f => `${f.username}: ${f.error}`).join(', ');
      const extra = failures.length > 3 ? ` and ${failures.length - 3} more` : '';
      toast.error(`All promotions failed — ${failMsgs}${extra}`);
    }

    setSelectedUsers([]);
    setShowPromoteForm(false);
    setPromoteRole('sso_admin');
    await fetchAdmins();
    setLoading(false);
  };

  const handleDemoteAdmin = async (admin) => {
    const confirmed = await confirm({
      title: 'Demote Admin',
      message: `Are you sure you want to demote admin user: ${admin.username}?`,
      confirmText: 'Demote',
      variant: 'warning',
    });
    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.demoteAdmin(admin.id);
      if (response.success) {
        toast.success(`Admin ${admin.username} demoted successfully`);
        await fetchAdmins();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeAdminRole = async (admin) => {
    const nextRole = isReadOnly(admin.role) ? 'sso_admin' : 'read_only';
    setLoading(true);
    try {
      await apiService.changeAdminRole(admin.id, nextRole);
      toast.success(`${admin.username} is now ${nextRole === 'read_only' ? 'read-only' : 'a full admin'}`);
      await fetchAdmins();
    } catch (err) {
      toast.error(err.message || 'Failed to change role');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCloudTrailAccess = async (admin) => {
    const newValue = !admin.cloudtrailAccess;
    try {
      const response = await apiService.toggleCloudTrailAccess(admin.id, newValue);
      if (response.success) {
        toast.success(`CloudTrail access ${newValue ? 'enabled' : 'disabled'} for ${admin.username}`);
        await fetchAdmins();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update CloudTrail access');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (!isPasswordValid(passwordForm.newPassword)) {
      toast.error(getPasswordErrorMessage(passwordForm.newPassword));
      return;
    }

    setChangingPassword(true);
    try {
      const response = await apiService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      if (response.success) {
        toast.success('Password changed successfully');
        setShowPasswordDialog(false);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchSsoSessionSettings = async () => {
    setLoadingSsoSettings(true);
    try {
      const response = await apiService.getSSOSessionSettings();
      if (response.success && response.data) {
        const timeoutMinutes = response.data.timeoutMinutes || 720;
        setSsoSessionTimeout(timeoutMinutes);
        setEditTimeoutValue(timeoutMinutes);
      }
    } catch (err) {
      console.error('Error fetching SSO session settings:', err);
    } finally {
      setLoadingSsoSettings(false);
    }
  };

  const handleUpdateSsoSettings = async () => {
    setSavingSsoSettings(true);
    try {
      const response = await apiService.updateSSOSessionSettings(editTimeoutValue);
      if (response.success) {
        toast.success('SSO session timeout updated successfully');
        setSsoSessionTimeout(editTimeoutValue);
        setShowSsoSettingsDialog(false);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update SSO session settings');
    } finally {
      setSavingSsoSettings(false);
    }
  };

  const formatTimeoutDisplay = (minutes) => {
    if (minutes >= 60) {
      const hours = minutes / 60;
      return hours === 1 ? '1 hour' : `${hours} hours`;
    }
    return `${minutes} minutes`;
  };

  const fetchMfaUsers = async () => {
    setLoadingMfaUsers(true);
    try {
      const response = await apiService.getUsersMFAStatus();
      if (response.success && response.data) {
        setMfaUsers(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error('Error fetching MFA users:', err);
    } finally {
      setLoadingMfaUsers(false);
    }
  };

  const fetchPasskeysSettings = async () => {
    setLoadingPasskey(true);
    try {
      const response = await apiService.getPasskeysSettings();
      if (response.success && response.data) {
        setPasskeyEnabled(Boolean(response.data.enabled));
      }
    } catch (err) {
      console.error('Error fetching passkeys settings:', err);
    } finally {
      setLoadingPasskey(false);
    }
  };

  const handleTogglePasskeys = async (enabled) => {
    setSavingPasskey(true);
    try {
      const response = await apiService.updatePasskeysSettings(enabled);
      if (response.success) {
        setPasskeyEnabled(enabled);
        if (!enabled && mfaMethod === 'passkey') setMfaMethod('otp');
        toast.success(enabled ? 'Passwordless passkeys enabled' : 'Passwordless passkeys disabled');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update passkeys settings');
    } finally {
      setSavingPasskey(false);
    }
  };

  const handleEnforceMFA = async () => {
    if (selectedMfaUsers.length === 0) return;
    setSavingMfa(true);
    try {
      const response = await apiService.enforceMFA(selectedMfaUsers, mfaMethod);
      if (response.success) {
        const label = mfaMethod === 'passkey' ? 'Passkey' : 'Authenticator app';
        toast.success(`${label} enforced for ${selectedMfaUsers.length} user(s)`);
        setSelectedMfaUsers([]);
        await fetchMfaUsers();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to enforce MFA');
    } finally {
      setSavingMfa(false);
    }
  };

  const handleRemoveMFAEnforcement = async () => {
    if (selectedMfaUsers.length === 0) return;
    setSavingMfa(true);
    try {
      const response = await apiService.removeMFAEnforcement(selectedMfaUsers, mfaMethod);
      if (response.success) {
        toast.success(`MFA enforcement removed for ${selectedMfaUsers.length} user(s)`);
        setSelectedMfaUsers([]);
        await fetchMfaUsers();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to remove MFA enforcement');
    } finally {
      setSavingMfa(false);
    }
  };

  const filteredMfaUsers = mfaUsers.filter(user => {
    if (!mfaSearchQuery) return true;
    const q = mfaSearchQuery.toLowerCase();
    return (user.username || '').toLowerCase().includes(q) ||
           (user.email || '').toLowerCase().includes(q) ||
           (user.firstName || '').toLowerCase().includes(q) ||
           (user.lastName || '').toLowerCase().includes(q);
  });

  const selectableMfaUsers = filteredMfaUsers.filter(u => !u.federated);

  const handleMfaUserToggle = (userId) => {
    const user = mfaUsers.find(u => u.id === userId);
    if (user?.federated) return;
    setSelectedMfaUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleMfaSelectAll = () => {
    if (selectedMfaUsers.length === selectableMfaUsers.length && selectableMfaUsers.length > 0) {
      setSelectedMfaUsers([]);
    } else {
      setSelectedMfaUsers(selectableMfaUsers.map(u => u.id));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const availableUsers = users.filter(user =>
    !admins.some(admin => admin.username === user.username)
  );

  // Minimalist select style
  const selectStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': { borderColor: '#E5E5E5' },
      '&:hover fieldset': { borderColor: '#9B59B6' },
      '&.Mui-focused fieldset': { borderColor: '#9B59B6' },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': { color: '#9B59B6' },
    },
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#9B59B6',
                width: 48,
                height: 48,
              }}
            >
              <AdminIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Admin Management
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                {admins.length} admin{admins.length !== 1 ? 's' : ''} in the system
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Tooltip title="Refresh Admins">
              <IconButton
                onClick={fetchAdmins}
                disabled={refreshing}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#F0F4F8',
                    borderColor: '#9B59B6',
                    color: '#9B59B6',
                  },
                  '&:disabled': {
                    color: '#BDBDBD',
                    backgroundColor: '#F9FBFD',
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#9B59B6' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>

            {hasMinRole(currentUser?.role, ROLES.ADMIN) && (
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenPromoteForm}
                sx={{
                  backgroundColor: '#9B59B6',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#8E44AD',
                  },
                }}
              >
                Promote User
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Admin List */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          p: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.1rem',
            color: '#253E66',
            mb: 3,
          }}
        >
          Current Admin Users
        </Typography>

        {refreshing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#9B59B6', mb: 2 }} />
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666' }}>
              Loading admin users...
            </Typography>
          </Box>
        ) : admins.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                    }}
                  >
                    Username
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                    }}
                  >
                    MFA
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                    }}
                  >
                    CloudTrail
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                    }}
                  >
                    Last Login
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#253E66',
                      borderBottom: '1px solid #E5E5E5',
                      width: 150,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin, index) => (
                  <TableRow
                    key={admin.id}
                    sx={{
                      '@keyframes fadeInRow': {
                        from: { opacity: 0, transform: 'translateY(8px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                      '&:hover': {
                        backgroundColor: '#F9FBFD',
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            backgroundColor: hasMinRole(admin.role, ROLES.ADMIN) ? '#FDF2F2' : isReadOnly(admin.role) ? '#EAF4FB' : '#F5EBF8',
                            color: hasMinRole(admin.role, ROLES.ADMIN) ? '#E74C3C' : isReadOnly(admin.role) ? '#3498DB' : '#9B59B6',
                            width: 36,
                            height: 36,
                          }}
                        >
                          {hasMinRole(admin.role, ROLES.ADMIN) ? <SecurityIcon sx={{ fontSize: 18 }} /> : isReadOnly(admin.role) ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <PersonIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: '#253E66',
                          }}
                        >
                          {admin.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Chip
                        label={hasMinRole(admin.role, ROLES.ADMIN) ? 'Super Admin' : isReadOnly(admin.role) ? 'Read-only' : 'Admin'}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          backgroundColor: hasMinRole(admin.role, ROLES.ADMIN) ? '#FDF2F2' : isReadOnly(admin.role) ? '#EAF4FB' : '#F5EBF8',
                          color: hasMinRole(admin.role, ROLES.ADMIN) ? '#E74C3C' : isReadOnly(admin.role) ? '#3498DB' : '#9B59B6',
                          border: `1px solid ${hasMinRole(admin.role, ROLES.ADMIN) ? '#E74C3C20' : isReadOnly(admin.role) ? '#3498DB20' : '#9B59B620'}`,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Chip
                        label={admin.mfaEnabled ? 'Enabled' : 'Disabled'}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          backgroundColor: admin.mfaEnabled ? '#F0F9F4' : '#FEF9E7',
                          color: admin.mfaEnabled ? '#27AE60' : '#F39C12',
                          border: `1px solid ${admin.mfaEnabled ? '#27AE6020' : '#F39C1220'}`,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Tooltip title={admin.cloudtrailAccess ? 'Disable CloudTrail access' : 'Enable CloudTrail access'}>
                        <Switch
                          size="small"
                          checked={!!admin.cloudtrailAccess}
                          onChange={() => handleToggleCloudTrailAccess(admin)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4698D3',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4698D3',
                            },
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.875rem',
                          color: '#666666',
                        }}
                      >
                        {formatDate(admin.lastLogin)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E5E5E5' }}>
                      {hasMinRole(admin.role, ROLES.ADMIN) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title={isReadOnly(currentUser?.role) ? "Read-only access — you don't have permission to make changes." : ''}>
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<LockIcon />}
                                onClick={() => setShowPasswordDialog(true)}
                                disabled={isReadOnly(currentUser?.role)}
                                sx={{
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                  width: 164,
                                  whiteSpace: 'nowrap',
                                  color: '#F39C12',
                                  borderColor: '#F39C1240',
                                  '&:hover': {
                                    borderColor: '#F39C12',
                                    backgroundColor: '#FEF9E7',
                                  },
                                  '&:disabled': {
                                    color: '#999999',
                                    borderColor: '#E5E5E5',
                                  },
                                }}
                              >
                                Change Password
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : hasMinRole(currentUser?.role, ROLES.ADMIN) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PersonRemoveIcon />}
                            onClick={() => handleDemoteAdmin(admin)}
                            disabled={loading}
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              py: 0.5,
                              width: 164,
                              whiteSpace: 'nowrap',
                              color: '#E74C3C',
                              borderColor: '#E74C3C40',
                              '&:hover': {
                                borderColor: '#E74C3C',
                                backgroundColor: '#FDF2F2',
                              },
                              '&:disabled': {
                                color: '#999999',
                                borderColor: '#E5E5E5',
                              },
                            }}
                          >
                            Demote
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={isReadOnly(admin.role) ? <PersonIcon /> : <VisibilityIcon />}
                            onClick={() => handleChangeAdminRole(admin)}
                            disabled={loading}
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              py: 0.5,
                              width: 164,
                              whiteSpace: 'nowrap',
                              color: '#4698D3',
                              borderColor: '#4698D340',
                              '&:hover': {
                                borderColor: '#4698D3',
                                backgroundColor: '#EAF4FB',
                              },
                              '&:disabled': {
                                color: '#999999',
                                borderColor: '#E5E5E5',
                              },
                            }}
                          >
                            {isReadOnly(admin.role) ? 'Make Full Admin' : 'Make Read-only'}
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#F5EBF8',
                margin: '0 auto',
                mb: 2,
              }}
            >
              <AdminIcon sx={{ fontSize: 32, color: '#9B59B6' }} />
            </Avatar>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                fontSize: '1.1rem',
                color: '#253E66',
                mb: 1,
              }}
            >
              No Admin Users Found
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.875rem',
                color: '#666666',
              }}
            >
              {hasMinRole(currentUser?.role, ROLES.ADMIN)
                ? 'Click "Promote User" to add your first admin user'
                : 'No admin users available'}
            </Typography>
          </Box>
        )}
      </Paper>
      </motion.div>

      {/* SSO Session Timeout Settings - Only for customer realms */}
      {isCustomerSubdomain() && (
        <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  backgroundColor: '#F5EBF8',
                  width: 40,
                  height: 40,
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 20, color: '#9B59B6' }} />
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#253E66',
                  }}
                >
                  SSO Session Timeout
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  Maximum duration users stay logged in before re-authentication is required
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => {
                setEditTimeoutValue(ssoSessionTimeout);
                setShowSsoSettingsDialog(true);
              }}
              disabled={loadingSsoSettings}
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#9B59B6',
                borderColor: '#9B59B640',
                '&:hover': {
                  borderColor: '#9B59B6',
                  backgroundColor: '#F5EBF8',
                },
              }}
            >
              Edit
            </Button>
          </Box>

          <Box
            sx={{
              backgroundColor: '#F9FBFD',
              borderRadius: '8px',
              border: '1px solid #E5E5E5',
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {loadingSsoSettings ? (
              <CircularProgress size={20} sx={{ color: '#9B59B6' }} />
            ) : (
              <>
                <AccessTimeIcon sx={{ color: '#9B59B6', fontSize: 24 }} />
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: '#999999',
                      mb: 0.5,
                    }}
                  >
                    Current timeout
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      color: '#253E66',
                    }}
                  >
                    {formatTimeoutDisplay(ssoSessionTimeout)}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Paper>
        </motion.div>
      )}

      {/* Passwordless Passkeys - Only for customer realms */}
      {isCustomerSubdomain() && (
        <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ backgroundColor: '#F5EBF8', width: 40, height: 40 }}>
                <FingerprintIcon sx={{ fontSize: 20, color: '#8E44AD' }} />
              </Avatar>
              <Box>
                <Typography
                  sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, fontSize: '1.1rem', color: '#253E66' }}
                >
                  Passwordless Passkeys
                </Typography>
                <Typography
                  sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#999999', fontWeight: 500 }}
                >
                  Let users sign in with a passkey instead of a password (admins enforce enrollment per user below)
                </Typography>
              </Box>
            </Box>
            {loadingPasskey ? (
              <CircularProgress size={20} sx={{ color: '#9B59B6' }} />
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={passkeyEnabled}
                    disabled={savingPasskey}
                    onChange={(e) => handleTogglePasskeys(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#8E44AD' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#8E44AD' },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#253E66' }}>
                    {passkeyEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                }
              />
            )}
          </Box>
        </Paper>
        </motion.div>
      )}

      {/* MFA Settings - Only for customer realms */}
      {isCustomerSubdomain() && (
        <motion.div variants={sectionVariants} style={{ marginTop: 24 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  backgroundColor: '#F5EBF8',
                  width: 40,
                  height: 40,
                }}
              >
                <SecurityIcon sx={{ fontSize: 20, color: '#8E44AD' }} />
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#253E66',
                  }}
                >
                  Multi-Factor Authentication
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontSize: '0.8rem',
                    color: '#999999',
                    fontWeight: 500,
                  }}
                >
                  Enforce MFA for specific users — select users below to require authenticator setup
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Enforced Users Summary */}
          {!loadingMfaUsers && mfaUsers.filter(u => u.mfaStatus === 'enforced' || u.mfaStatus === 'configured').length > 0 && (
            <Box
              sx={{
                backgroundColor: '#F9FBFD',
                borderRadius: '8px',
                border: '1px solid #E5E5E5',
                mb: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: '1px solid #E5E5E5',
                  backgroundColor: '#F0F0F5',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: '#253E66',
                  }}
                >
                  Users with MFA Enforced / Configured ({mfaUsers.filter(u => u.mfaStatus === 'enforced' || u.mfaStatus === 'configured').length})
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {mfaUsers
                  .filter(u => u.mfaStatus === 'enforced' || u.mfaStatus === 'configured')
                  .map((user, index) => (
                    <Box
                      key={user.id}
                      sx={{
                        '@keyframes fadeInItem': {
                          from: { opacity: 0, transform: 'translateY(8px)' },
                          to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #F0F0F0',
                        '&:last-child': { borderBottom: 0 },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: '0.75rem',
                          backgroundColor: user.mfaStatus === 'configured' ? '#F0F9F4' : '#FEF9E7',
                          color: user.mfaStatus === 'configured' ? '#27AE60' : '#F39C12',
                          mr: 1.5,
                        }}
                      >
                        {(user.username || '?')[0].toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: '#253E66',
                            lineHeight: 1.3,
                          }}
                          noWrap
                        >
                          {user.username}
                        </Typography>
                        {user.email && (
                          <Typography
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontSize: '0.75rem',
                              color: '#999999',
                              lineHeight: 1.2,
                            }}
                            noWrap
                          >
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={user.mfaStatus === 'configured' ? 'Configured' : 'Pending Setup'}
                        size="small"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          ml: 1,
                          backgroundColor: user.mfaStatus === 'configured' ? '#F0F9F4' : '#FEF9E7',
                          color: user.mfaStatus === 'configured' ? '#27AE60' : '#F39C12',
                          border: `1px solid ${user.mfaStatus === 'configured' ? '#27AE6020' : '#F39C1220'}`,
                        }}
                      />
                      {user.factors && user.factors.passkey && user.factors.passkey !== 'none' && (
                        <Chip
                          icon={<FingerprintIcon sx={{ fontSize: 14 }} />}
                          label={user.factors.passkey === 'configured' ? 'Passkey' : 'Passkey pending'}
                          size="small"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            ml: 0.5,
                            backgroundColor: '#F5EBF8',
                            color: '#8E44AD',
                            border: '1px solid #8E44AD20',
                            '& .MuiChip-icon': { color: '#8E44AD' },
                          }}
                        />
                      )}
                      <Tooltip title={user.mfaStatus === 'configured' ? 'Remove MFA' : 'Remove MFA enforcement'}>
                          <IconButton
                            size="small"
                            disabled={savingMfa}
                            onClick={async () => {
                              setSavingMfa(true);
                              try {
                                // Remove whichever factor(s) this user actually has
                                // (authenticator and/or passkey), not just the default.
                                const factors = user.factors || {};
                                const present = ['otp', 'passkey'].filter(
                                  (m) => factors[m] && factors[m] !== 'none'
                                );
                                const toRemove = present.length > 0 ? present : ['otp', 'passkey'];
                                let ok = true;
                                for (const method of toRemove) {
                                  const response = await apiService.removeMFAEnforcement([user.id], method);
                                  if (!response.success) ok = false;
                                }
                                if (ok) {
                                  toast.success(
                                    user.mfaStatus === 'configured'
                                      ? `MFA removed for ${user.username}`
                                      : `MFA enforcement removed for ${user.username}`
                                  );
                                  await fetchMfaUsers();
                                }
                              } catch (err) {
                                toast.error(err.message || 'Failed to remove MFA');
                              } finally {
                                setSavingMfa(false);
                              }
                            }}
                            sx={{
                              ml: 0.5,
                              color: '#E74C3C',
                              '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.08)' },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                    </Box>
                  ))}
              </Box>
            </Box>
          )}

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search users by name or email..."
            value={mfaSearchQuery}
            onChange={(e) => setMfaSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.875rem',
                borderRadius: '8px',
              },
            }}
          />

          {/* User list */}
          <Box
            sx={{
              backgroundColor: '#F9FBFD',
              borderRadius: '8px',
              border: '1px solid #E5E5E5',
              maxHeight: 300,
              overflowY: 'auto',
            }}
          >
            {loadingMfaUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#8E44AD' }} />
              </Box>
            ) : filteredMfaUsers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#999999' }}>
                  {mfaSearchQuery ? 'No users match your search' : 'No users found'}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Select All (only counts selectable/non-federated users) */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderBottom: '1px solid #E5E5E5',
                    backgroundColor: '#F0F0F5',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedMfaUsers.length === selectableMfaUsers.length && selectableMfaUsers.length > 0}
                    indeterminate={selectedMfaUsers.length > 0 && selectedMfaUsers.length < selectableMfaUsers.length}
                    onChange={handleMfaSelectAll}
                    disabled={selectableMfaUsers.length === 0}
                    sx={{
                      color: '#8E44AD',
                      '&.Mui-checked': { color: '#8E44AD' },
                      '&.MuiCheckbox-indeterminate': { color: '#8E44AD' },
                    }}
                  />
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#666666' }}>
                    Select All ({selectableMfaUsers.length})
                  </Typography>
                </Box>

                {filteredMfaUsers.map((user, index) => (
                  <Box
                    key={user.id}
                    sx={{
                      '@keyframes fadeInItem': {
                        from: { opacity: 0, transform: 'translateY(8px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.75,
                      borderBottom: '1px solid #F0F0F0',
                      '&:hover': { backgroundColor: user.federated ? 'transparent' : '#F5EBF820' },
                      cursor: user.federated ? 'default' : 'pointer',
                      opacity: user.federated ? 0.6 : 1,
                    }}
                    onClick={() => !user.federated && handleMfaUserToggle(user.id)}
                  >
                    <Checkbox
                      size="small"
                      checked={!user.federated && selectedMfaUsers.includes(user.id)}
                      disabled={user.federated}
                      sx={{
                        color: '#8E44AD',
                        '&.Mui-checked': { color: '#8E44AD' },
                        '&.Mui-disabled': { color: '#E5E5E5' },
                      }}
                    />
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        backgroundColor: user.federated ? '#E5E5E5' : '#F5EBF8',
                        fontSize: '0.75rem',
                        color: user.federated ? '#999999' : '#8E44AD',
                        mr: 1.5,
                      }}
                    >
                      {(user.username || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          color: user.federated ? '#999999' : '#253E66',
                          lineHeight: 1.3,
                        }}
                        noWrap
                      >
                        {user.username}
                      </Typography>
                      {user.email && (
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontSize: '0.75rem',
                            color: '#999999',
                            lineHeight: 1.2,
                          }}
                          noWrap
                        >
                          {user.email}
                        </Typography>
                      )}
                    </Box>
                    {user.federated ? (
                      <Chip
                        label="IdP-Linked"
                        size="small"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          ml: 1,
                          backgroundColor: '#EBF5FB',
                          color: '#2980B9',
                          border: '1px solid #2980B920',
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                        <Chip
                          label={
                            user.mfaStatus === 'configured' ? 'Configured' :
                            user.mfaStatus === 'enforced' ? 'Pending Setup' :
                            'Not Enforced'
                          }
                          size="small"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            backgroundColor:
                              user.mfaStatus === 'configured' ? '#F0F9F4' :
                              user.mfaStatus === 'enforced' ? '#FEF9E7' :
                              '#F5F5F5',
                            color:
                              user.mfaStatus === 'configured' ? '#27AE60' :
                              user.mfaStatus === 'enforced' ? '#F39C12' :
                              '#999999',
                            border: `1px solid ${
                              user.mfaStatus === 'configured' ? '#27AE6020' :
                              user.mfaStatus === 'enforced' ? '#F39C1220' :
                              '#E5E5E5'
                            }`,
                          }}
                        />
                        {user.factors && user.factors.passkey && user.factors.passkey !== 'none' && (
                          <Chip
                            icon={<FingerprintIcon sx={{ fontSize: 14 }} />}
                            label={user.factors.passkey === 'configured' ? 'Passkey' : 'Passkey pending'}
                            size="small"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              backgroundColor: '#F5EBF8',
                              color: '#8E44AD',
                              border: '1px solid #8E44AD20',
                              '& .MuiChip-icon': { color: '#8E44AD' },
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, alignItems: 'center' }}>
            <Select
              size="small"
              value={mfaMethod}
              onChange={(e) => setMfaMethod(e.target.value)}
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.875rem',
                borderRadius: '6px',
                minWidth: 180,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E5E5' },
              }}
            >
              <MenuItem value="otp" sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                Authenticator app
              </MenuItem>
              <MenuItem
                value="passkey"
                disabled={!passkeyEnabled}
                sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}
              >
                Passkey (passwordless){!passkeyEnabled ? ' (enable above)' : ''}
              </MenuItem>
            </Select>
            <Button
              variant="contained"
              size="small"
              onClick={handleEnforceMFA}
              disabled={selectedMfaUsers.length === 0 || savingMfa}
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: '#8E44AD',
                '&:hover': { backgroundColor: '#7D3C98' },
                '&.Mui-disabled': { backgroundColor: '#E5E5E5', color: '#999999' },
              }}
            >
              {savingMfa ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null}
              Enforce
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRemoveMFAEnforcement}
              disabled={selectedMfaUsers.length === 0 || savingMfa}
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#8E44AD',
                borderColor: '#8E44AD40',
                '&:hover': { borderColor: '#8E44AD', backgroundColor: '#F5EBF8' },
              }}
            >
              Remove
            </Button>
          </Box>

          <Alert
            severity="info"
            sx={{
              mt: 2,
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.8rem',
              borderRadius: '6px',
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          >
            Enforcing requires the selected users to set up the chosen factor (authenticator app or security key) at their next login.
            Removing clears that factor's pending requirement and deletes its credential; users keep any other configured factor.
          </Alert>
        </Paper>
        </motion.div>
      )}

      {/* Promote User Modal */}
      <Dialog
        open={showPromoteForm}
        onClose={() => { setShowPromoteForm(false); setSelectedUsers([]); setPromoteRole('sso_admin'); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#F5EBF8',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#253E66',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 3,
            position: 'relative',
          }}
        >
          <Avatar sx={{ backgroundColor: '#9B59B6', width: 40, height: 40 }}>
            <PersonAddIcon />
          </Avatar>
          Promote Users to Admin
          <IconButton
            onClick={() => { setShowPromoteForm(false); setPromoteRole('sso_admin'); }}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666666',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: '#FFFFFF' }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.875rem',
                color: '#666666',
                mb: 3,
              }}
            >
              Search and select one or more users below to promote to admin role.
            </Typography>

            {fetchingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#9B59B6' }} />
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', ml: 2 }}>
                  Loading users...
                </Typography>
              </Box>
            ) : availableUsers.length > 0 ? (
              <Autocomplete
                multiple
                options={availableUsers}
                value={selectedUsers}
                onChange={(_, newValue) => setSelectedUsers(newValue)}
                getOptionLabel={(option) => option.username}
                isOptionEqualToValue={(option, value) => option.username === value.username}
                disableCloseOnSelect
                noOptionsText="No users available to promote"
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.toLowerCase();
                  return options.filter(
                    (o) => o.username.toLowerCase().includes(q) || (o.email && o.email.toLowerCase().includes(q))
                  );
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id || option.username}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      sx={{
                        color: '#9B59B6',
                        '&.Mui-checked': { color: '#9B59B6' },
                        mr: 1,
                      }}
                    />
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        backgroundColor: '#F5EBF8',
                        color: '#9B59B6',
                        mr: 1.5,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66', fontSize: '0.875rem' }}>
                        {option.username}
                      </Typography>
                      {option.email && (
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' }}>
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id || option.username}
                      label={option.username}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(155, 89, 182, 0.1)',
                        border: '1px solid rgba(155, 89, 182, 0.2)',
                        color: '#9B59B6',
                        borderRadius: '4px',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        '& .MuiChip-deleteIcon': {
                          color: '#9B59B6',
                          '&:hover': { color: '#8E44AD' },
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={selectedUsers.length === 0 ? "Search users..." : ""}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ color: '#999999', fontSize: 18, ml: 0.5, mr: 0.5, flexShrink: 0 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={selectStyle}
                  />
                )}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      mt: 0.5,
                      '& .MuiAutocomplete-option': {
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: '#F9FBFD',
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'rgba(155, 89, 182, 0.1)',
                          '&:hover': {
                            backgroundColor: 'rgba(155, 89, 182, 0.15)',
                          },
                        },
                      },
                      '& .MuiAutocomplete-noOptions': {
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#999999',
                        fontStyle: 'italic',
                      },
                    },
                  },
                }}
              />
            ) : (
              <Alert
                severity="info"
                sx={{
                  backgroundColor: '#EBF5FB',
                  border: '1px solid #4698D320',
                  '& .MuiAlert-icon': { color: '#4698D3' },
                }}
              >
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#253E66' }}>
                  No users available to promote. All users are already administrators.
                </Typography>
              </Alert>
            )}

            {/* Access level picker */}
            <Box sx={{ mt: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#253E66',
                  mb: 1,
                }}
              >
                Access level
              </Typography>
              <FormControl fullWidth size="small" sx={selectStyle}>
                <Select
                  value={promoteRole}
                  onChange={(e) => setPromoteRole(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="sso_admin">
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      Full admin — can manage all resources
                    </Typography>
                  </MenuItem>
                  <MenuItem value="read_only">
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      Read-only — can view but not change anything
                    </Typography>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          </motion.div>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E5E5',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => { setShowPromoteForm(false); setPromoteRole('sso_admin'); }}
            disabled={loading}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#F9FBFD',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePromoteUser}
            disabled={loading || selectedUsers.length === 0 || availableUsers.length === 0}
            startIcon={loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <PersonAddIcon />}
            sx={{
              backgroundColor: '#9B59B6',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              '&:hover': {
                backgroundColor: '#8E44AD',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
          >
            {loading ? 'Promoting...' : selectedUsers.length > 1 ? `Promote ${selectedUsers.length} to Admin` : 'Promote to Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={showPasswordDialog}
        onClose={() => {
          setShowPasswordDialog(false);
          setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#FEF9E7',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#253E66',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 3,
            position: 'relative',
          }}
        >
          <Avatar sx={{ backgroundColor: '#F39C12', width: 40, height: 40 }}>
            <LockIcon />
          </Avatar>
          Change Password
          <IconButton
            onClick={() => {
              setShowPasswordDialog(false);
              setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666666',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Box component="form" onSubmit={handlePasswordChange} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  required
                  disabled={changingPassword}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'Metropolis, sans-serif',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': { borderColor: '#E5E5E5' },
                      '&:hover fieldset': { borderColor: '#F39C12' },
                      '&.Mui-focused fieldset': { borderColor: '#F39C12' },
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Metropolis, sans-serif',
                      color: '#666666',
                      '&.Mui-focused': { color: '#F39C12' },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  required
                  disabled={changingPassword}
                  inputProps={{ minLength: 8 }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'Metropolis, sans-serif',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': { borderColor: '#E5E5E5' },
                      '&:hover fieldset': { borderColor: '#F39C12' },
                      '&.Mui-focused fieldset': { borderColor: '#F39C12' },
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Metropolis, sans-serif',
                      color: '#666666',
                      '&.Mui-focused': { color: '#F39C12' },
                    },
                  }}
                />
                <PasswordStrengthIndicator password={passwordForm.newPassword} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required
                  disabled={changingPassword}
                  error={passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword}
                  helperText={
                    passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'Metropolis, sans-serif',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': { borderColor: '#E5E5E5' },
                      '&:hover fieldset': { borderColor: '#F39C12' },
                      '&.Mui-focused fieldset': { borderColor: '#F39C12' },
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Metropolis, sans-serif',
                      color: '#666666',
                      '&.Mui-focused': { color: '#F39C12' },
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: 'Metropolis, sans-serif',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          </motion.div>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E5E5',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => {
              setShowPasswordDialog(false);
              setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }}
            disabled={changingPassword}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#F9FBFD',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePasswordChange}
            disabled={
              changingPassword ||
              !passwordForm.currentPassword ||
              !passwordForm.newPassword ||
              !passwordForm.confirmPassword ||
              passwordForm.newPassword !== passwordForm.confirmPassword ||
              !isPasswordValid(passwordForm.newPassword)
            }
            startIcon={changingPassword ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <LockIcon />}
            sx={{
              backgroundColor: '#F39C12',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              '&:hover': {
                backgroundColor: '#E67E22',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
          >
            {changingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SSO Session Timeout Dialog */}
      <Dialog
        open={showSsoSettingsDialog}
        onClose={() => setShowSsoSettingsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#F5EBF8',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#253E66',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2,
            px: 3,
            position: 'relative',
          }}
        >
          <Avatar sx={{ backgroundColor: '#9B59B6', width: 40, height: 40 }}>
            <AccessTimeIcon />
          </Avatar>
          Edit SSO Session Timeout
          <IconButton
            onClick={() => setShowSsoSettingsDialog(false)}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666666',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              '&:hover': { backgroundColor: '#F9FBFD' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              color: '#666666',
              mb: 3,
            }}
          >
            Set the maximum duration that users can remain logged in before they need to re-authenticate.
          </Typography>

          <FormControl fullWidth size="small" sx={selectStyle}>
            <InputLabel>Session Timeout</InputLabel>
            <Select
              value={editTimeoutValue}
              onChange={(e) => setEditTimeoutValue(e.target.value)}
              label="Session Timeout"
            >
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={60}>1 hour</MenuItem>
              <MenuItem value={240}>4 hours</MenuItem>
              <MenuItem value={480}>8 hours</MenuItem>
              <MenuItem value={720}>12 hours (default)</MenuItem>
            </Select>
          </FormControl>
          </motion.div>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E5E5',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setShowSsoSettingsDialog(false)}
            disabled={savingSsoSettings}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              color: '#666666',
              borderColor: '#E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#F9FBFD',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateSsoSettings}
            disabled={savingSsoSettings || editTimeoutValue === ssoSessionTimeout}
            startIcon={savingSsoSettings ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <AccessTimeIcon />}
            sx={{
              backgroundColor: '#9B59B6',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              px: 3,
              '&:hover': {
                backgroundColor: '#8E44AD',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E5E5',
                color: '#999999',
              },
            }}
          >
            {savingSsoSettings ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default AdminManagement;
